/**
 * Repositorio: única vía de escritura. Cada escritura:
 *  1. genera marcas HLC por campo,
 *  2. fusiona con el registro existente,
 *  3. valida el registro completo con el esquema compartido,
 *  4. guarda y, si la sincronización está activa, encola la operación,
 * todo dentro de una transacción de IndexedDB.
 */
import {
  ENTITIES,
  HybridClock,
  mergeFields,
  mergeRecords,
  validateRecord,
  type EntityName,
  type ExportSource,
  type FieldWrite,
  type StoredRecord,
} from '@habitos/protocol';
import { shortId, uuidv7 } from '../lib/ids';
import { db, getMeta, setMeta, type LocalRecord, type OutboxItem } from './db';

export class ValidationError extends Error {
  constructor(
    readonly entity: EntityName,
    readonly detail: string,
  ) {
    super(`Datos no válidos (${entity}): ${detail}`);
  }
}

let clock: HybridClock | null = null;
let deviceIdCache: string | null = null;

export async function deviceId(): Promise<string> {
  if (deviceIdCache) return deviceIdCache;
  let id = await getMeta<string | null>('deviceId', null);
  if (!id) {
    id = shortId();
    await setMeta('deviceId', id);
  }
  deviceIdCache = id;
  return id;
}

async function getClock(): Promise<HybridClock> {
  if (clock) return clock;
  const node = await deviceId();
  const last = await getMeta<string | undefined>('hlc', undefined);
  clock = new HybridClock(node, () => Date.now(), last);
  return clock;
}

/** Solo para pruebas. */
export function __resetRepoState(): void {
  clock = null;
  deviceIdCache = null;
}

export async function isSyncEnabled(): Promise<boolean> {
  return (await getMeta<string | null>('accountUserId', null)) !== null;
}

export type Patch = Record<string, unknown>;

export interface WriteRequest {
  entity: EntityName;
  id: string;
  patch: Patch;
}

/** Aplica varias escrituras de forma atómica. */
export type { WriteTrace } from '@habitos/assistant/domain/records';
import type { WriteTrace } from '@habitos/assistant/domain/records';

let recorder: WriteTrace[] | null = null;

/** Ejecuta `fn` anotando todas las escrituras locales que hace. No admite anidarse. */
export async function recordWrites<T>(fn: () => Promise<T>): Promise<{ result: T; writes: WriteTrace[] }> {
  if (recorder) throw new Error('recordWrites_nested');
  const writes: WriteTrace[] = [];
  recorder = writes;
  try {
    const result = await fn();
    return { result, writes };
  } finally {
    recorder = null;
  }
}

function trace(entity: EntityName, id: string, existing: { data: Record<string, unknown> } | undefined, after: Record<string, unknown>, keys: string[]): void {
  if (!recorder) return;
  const prev = recorder.find((w) => w.entity === entity && w.id === id);
  if (prev) {
    // Varias escrituras sobre el mismo registro: se conserva el "antes" de la primera.
    if (!prev.created) {
      for (const k of keys) {
        if (!(k in prev.before)) prev.before[k] = existing?.data[k] ?? null;
        prev.after[k] = after[k] ?? null;
      }
    }
    return;
  }
  if (!existing) {
    recorder.push({ entity, id, created: true, before: {}, after: {} });
    return;
  }
  recorder.push({
    entity,
    id,
    created: false,
    before: Object.fromEntries(keys.map((k) => [k, existing.data[k] ?? null])),
    after: Object.fromEntries(keys.map((k) => [k, after[k] ?? null])),
  });
}

export async function writeMany(requests: WriteRequest[]): Promise<void> {
  if (requests.length === 0) return;
  const c = await getClock();
  const sync = await isSyncEnabled();
  const tables = [...new Set(requests.map((r) => db.table_(r.entity)))];
  await db.transaction('rw', [...tables, db.outbox, db.meta], async () => {
    for (const req of requests) {
      const table = db.table_(req.entity);
      const existing = await table.get(req.id);
      const fields: Record<string, FieldWrite> = {};
      for (const [k, v] of Object.entries(req.patch)) fields[k] = [v === undefined ? null : v, c.tick()];
      const { record, changed } = mergeFields(
        existing ? { entity: req.entity, ...existing } : null,
        req.entity,
        req.id,
        fields,
      );
      if (!changed) continue;
      const valid = validateRecord(req.entity, req.id, record.data);
      if (!valid.ok) throw new ValidationError(req.entity, valid.detail);
      await table.put({ id: req.id, data: record.data, hlc: record.hlc });
      trace(req.entity, req.id, existing as { data: Record<string, unknown> } | undefined, record.data as Record<string, unknown>, Object.keys(req.patch));
      if (sync) {
        await db.outbox.add({ opId: uuidv7(), entity: req.entity, id: req.id, fields, createdAt: Date.now(), attempts: 0 });
      }
    }
    await setMeta('hlc', c.current());
  });
}

export async function write(entity: EntityName, id: string, patch: Patch): Promise<void> {
  await writeMany([{ entity, id, patch }]);
}

export interface ImportPreview {
  /** Registros que no existen en el dispositivo. */
  added: number;
  /** Registros existentes en los que el archivo cambiaría algún campo (por tener cambios más recientes). */
  updated: number;
  unchanged: number;
}

/**
 * Estado actual de los registros afectados, leído en bloque por entidad (una lectura
 * por tabla en lugar de una por registro: decisivo en archivos grandes).
 */
async function loadCurrent(records: StoredRecord[]): Promise<Map<EntityName, Map<string, LocalRecord | undefined>>> {
  const out = new Map<EntityName, Map<string, LocalRecord | undefined>>();
  const ids = new Map<EntityName, Set<string>>();
  for (const r of records) {
    if (!ids.has(r.entity)) ids.set(r.entity, new Set());
    ids.get(r.entity)!.add(r.id);
  }
  for (const [entity, set] of ids) {
    const list = [...set];
    const rows = await db.table_(entity).bulkGet(list);
    out.set(entity, new Map(list.map((id, i) => [id, rows[i]])));
  }
  return out;
}

/**
 * Simula la fusión de una importación sin escribir nada (confirmación previa).
 * Sigue el estado en memoria, así que un registro repetido en el archivo se cuenta
 * igual que se aplicaría.
 */
export async function previewImport(records: StoredRecord[]): Promise<ImportPreview> {
  const out: ImportPreview = { added: 0, updated: 0, unchanged: 0 };
  const current = await loadCurrent(records);
  for (const remote of records) {
    const byId = current.get(remote.entity)!;
    const existing = byId.get(remote.id);
    const res = mergeRecords(existing ? { entity: remote.entity, ...existing } : null, remote);
    if (!existing) out.added++;
    else if (res.changed) out.updated++;
    else out.unchanged++;
    if (res.changed) byId.set(remote.id, { id: remote.id, data: res.record.data, hlc: res.record.hlc });
  }
  return out;
}

/** El archivo procede de otra cuenta que la propietaria de los datos del dispositivo. */
export function importOwnerMismatch(source: ExportSource | null, ownerId: string | null): boolean {
  return Boolean(source?.accountId && ownerId && source.accountId !== ownerId);
}

/** Fusiona registros recibidos del servidor o de una importación. */
export async function applyRemote(records: StoredRecord[], options: { enqueue: boolean }): Promise<number> {
  const c = await getClock();
  let changed = 0;
  const entities = [...new Set(records.map((r) => r.entity))];
  const tables = entities.map((e) => db.table_(e));
  await db.transaction('rw', [...tables, db.outbox, db.meta], async () => {
    // Lectura y escritura en bloque; el estado intermedio se sigue en memoria para que
    // un registro repetido en la entrada se fusione sobre su versión anterior.
    const current = await loadCurrent(records);
    const dirty = new Map<EntityName, Map<string, LocalRecord>>();
    const ops: OutboxItem[] = [];
    for (const remote of records) {
      const byId = current.get(remote.entity)!;
      const existing = byId.get(remote.id);
      for (const stamp of Object.values(remote.hlc)) c.receive(stamp);
      const res = mergeRecords(existing ? { entity: remote.entity, ...existing } : null, remote);
      if (!res.changed) continue;
      const row = { id: remote.id, data: res.record.data, hlc: res.record.hlc };
      byId.set(remote.id, row);
      if (!dirty.has(remote.entity)) dirty.set(remote.entity, new Map());
      dirty.get(remote.entity)!.set(remote.id, row);
      changed++;
      if (options.enqueue) {
        const fields: Record<string, FieldWrite> = {};
        for (const [k, v] of Object.entries(res.record.data)) {
          const stamp = res.record.hlc[k];
          if (stamp) fields[k] = [v, stamp];
        }
        ops.push({ opId: uuidv7(), entity: remote.entity, id: remote.id, fields, createdAt: Date.now(), attempts: 0 });
      }
    }
    for (const [entity, rows] of dirty) await db.table_(entity).bulkPut([...rows.values()]);
    if (ops.length) await db.outbox.bulkAdd(ops);
    await setMeta('hlc', c.current());
  });
  return changed;
}

export async function allRecords(): Promise<StoredRecord[]> {
  const out: StoredRecord[] = [];
  for (const entity of ENTITIES) {
    const rows: LocalRecord[] = await db.table_(entity).toArray();
    for (const r of rows) out.push({ entity, id: r.id, data: r.data, hlc: r.hlc });
  }
  return out;
}

export async function hasLocalData(): Promise<boolean> {
  return (await db.habits.count()) > 0 || (await db.entries.count()) > 0;
}

/** Borra todos los datos locales del dispositivo (cerrar sesión con borrado, eliminar cuenta). */
export async function wipeLocalData(): Promise<void> {
  const tables = ENTITIES.map((e) => db.table_(e));
  await db.transaction('rw', [...tables, db.outbox, db.rejected, db.meta], async () => {
    await Promise.all([
      ...tables.map((t) => t.clear()),
      db.outbox.clear(),
      db.rejected.clear(),
    ]);
    const keep = new Set(['deviceId', 'hlc', 'theme', 'locale']);
    for (const row of await db.meta.toArray()) if (!keep.has(row.key)) await db.meta.delete(row.key);
  });
}
